import { CommonModule, DatePipe, NgComponentOutlet } from '@angular/common';
import { Component, computed, inject, input, Type } from '@angular/core';
import { contactLink } from '@web/shared/pipes/contact-link.pipe';
import { employeeLink } from '@web/shared/pipes/employee-link.pipe';
import { realEstateLink } from '@web/shared/pipes/real-estate-link.pipe';
import { supplierLink } from '@web/shared/pipes/supplier-link.pipe';
import { isEmpty } from 'class-validator';
import { TagModule } from 'primeng/tag';
import { ContactCodeTagComponent } from '../../contact-code-tag.component';
import { EmployeeCodeTagComponent } from '../../employee-code-tag.component';
import { LeadCodeTagComponent } from '../../lead-code-tag.component';
import { OwnerCompanyCodeTagComponent } from '../../owner-company-code-tag.component';
import { RealEstateCodeTagComponent } from '../../real-estate-code-tag.component';
import { ScoutingCodeTagComponent } from '../../scouting-code-tag.component';
import { SupplierCodeTagComponent } from '../../supplier-code-tag.component';
import { SearchNoteEntry } from '../interfaces/search-notes-entry.interface';
import { DetailItem, SearchItemBaseComponent } from './search-item-base.component';

@Component({
  selector: 'app-search-note-item',
  imports: [CommonModule, TagModule, NgComponentOutlet, SearchItemBaseComponent],
  providers: [DatePipe],
  template: `
    @let _item = item();

    <app-search-item-base
      icon="pi pi-thumbtack"
      [detailRouterLink]="link()"
      [color]="iconColor[_item.relatedTo.aggregate]"
      [detailItems]="detailItems()"
    >
      <ng-template #title>
        <div [innerHTML]="_item.title"></div>
      </ng-template>

      <ng-template #codeTag>
        @let tagComponents = codeTagComponents[_item.relatedTo.aggregate];
        <ng-container *ngComponentOutlet="tagComponents[0]; inputs: tagComponents[1](_item)"> </ng-container>
      </ng-template>
    </app-search-item-base>
  `,
})
export class SearchNoteItemComponent {
  readonly item = input.required<SearchNoteEntry>();

  readonly datePipe = inject(DatePipe);

  readonly detailItems = computed<DetailItem[]>(() => {
    const item = this.item();

    return [
      {
        icon: 'pi pi-pen-to-square',
        content: item.body,
        isHtml: true,
      },
      {
        icon: 'pi pi-clock',
        content: this.datePipe.transform(item.createdAt) || '',
        isHtml: false,
      },
    ].filter((v) => !isEmpty(v.content));
  });

  readonly link = computed(() => {
    const item = this.item();
    const links: Record<SearchNoteEntry['relatedTo']['aggregate'], () => string[]> = {
      Contact: () =>
        contactLink({ id: item.relatedTo.id, kind: item.relatedTo.kind as 'naturalPerson' | 'legalPerson' }, ['notes']),
      RealEstate: () =>
        realEstateLink({ id: item.relatedTo.id, kind: item.relatedTo.kind as 'unit' | 'group' }, ['notes']),
      Lead: () => ['/', 'leads', item.relatedTo.id, 'notes'],
      Scouting: () => ['/', 'scoutings', item.relatedTo.id, 'notes'],
      Employee: () => employeeLink({ id: item.relatedTo.id }, ['notes']),
      Supplier: () =>
        supplierLink({ id: item.relatedTo.id, kind: item.relatedTo.kind as 'company' | 'freelance' }, ['notes']),
      OwnerCompany: () => ['/', 'owner-company'],
    };

    return links[item.relatedTo.aggregate]();
  });

  readonly codeTagComponents: Record<
    SearchNoteEntry['relatedTo']['aggregate'],
    [Type<unknown>, (item: SearchNoteEntry) => Record<string, unknown>]
  > = {
    Contact: [ContactCodeTagComponent, (item: SearchNoteEntry) => ({ contact: item.relatedTo, clickable: false })],
    Lead: [LeadCodeTagComponent, (item: SearchNoteEntry) => ({ lead: item.relatedTo, clickable: false })],
    RealEstate: [
      RealEstateCodeTagComponent,
      (item: SearchNoteEntry) => ({ realEstate: item.relatedTo, clickable: false }),
    ],
    Scouting: [ScoutingCodeTagComponent, (item: SearchNoteEntry) => ({ scouting: item.relatedTo, clickable: false })],
    Employee: [EmployeeCodeTagComponent, (item: SearchNoteEntry) => ({ employee: item.relatedTo, clickable: false })],
    Supplier: [SupplierCodeTagComponent, (item: SearchNoteEntry) => ({ supplier: item.relatedTo, clickable: false })],
    OwnerCompany: [OwnerCompanyCodeTagComponent, () => ({ clickable: false })],
  };

  readonly iconColor: Record<SearchNoteEntry['relatedTo']['aggregate'], string> = {
    Contact: 'text-soe-twilight-blue',
    Lead: 'text-soe-tropical-green',
    RealEstate: 'text-soe-orange-glow',
    Scouting: 'text-soe-golden-sand',
    Employee: 'text-soe-orchid-purple',
    Supplier: 'text-soe-ocean-blue',
    OwnerCompany: 'text-soe-ocean-blue',
  };
}
